/**
 * DV-TEMP-002: Stage Selector Component
 * Dropdown selector for choosing which stage to view heartbeats and QC summary
 */

import { useState, useCallback } from 'react';
import { ChevronDown, Activity } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Stage, type StageName } from '@/lib/shared-types';

export interface StageSelectorProps {
  selectedStage: StageName;
  onStageChange: (stage: StageName) => void;
  className?: string;
  'data-testid'?: string;
}

// Stage options with labels - derived from Zod schema
const STAGE_OPTIONS: { value: StageName; label: string; description: string }[] = [
  { value: 'script', label: 'Script', description: 'Script generation and review' },
  { value: 'vision', label: 'Vision', description: 'Visual concept development' },
  { value: 'storyboard', label: 'Storyboard', description: 'Scene planning and layout' },
  { value: 'shots', label: 'Shots', description: 'Shot generation and composition' },
  { value: 'continuity', label: 'Continuity', description: 'Continuity and consistency' },
  { value: 'frames', label: 'Frames', description: 'Frame generation and rendering' },
  { value: 'videos', label: 'Videos', description: 'Video creation and processing' },
  { value: 'music', label: 'Music', description: 'Audio and music generation' },
];

export function StageSelector({
  selectedStage,
  onStageChange,
  className,
  'data-testid': testId = 'stage-selector'
}: StageSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = STAGE_OPTIONS.find(option => option.value === selectedStage) || STAGE_OPTIONS[0];

  const handleSelect = useCallback((stage: StageName) => {
    // Validate stage before selection
    if (!Stage.options.includes(stage)) {
      console.warn(`Invalid stage attempted: ${stage}`);
      return;
    }

    onStageChange(stage);
    setIsOpen(false);
  }, [onStageChange]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent, stage?: StageName) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (stage) {
        handleSelect(stage);
      } else {
        setIsOpen(!isOpen);
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  }, [isOpen, handleSelect]);

  return (
    <div className={cn("relative", className)} data-testid={testId}>
      <label
        className="text-xs font-mono uppercase tracking-wider block mb-2"
        style={{
          fontFamily: 'var(--font-family-mono)',
          color: 'var(--fg-secondary)',
        }}
      >
        Pipeline Stage
      </label>

      {/* Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => handleKeyDown(e)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded border transition-colors focus:outline-none focus:ring-2"
        style={{
          backgroundColor: 'var(--bg-elevated)',
          borderColor: 'var(--border-default)',
          color: 'var(--fg-primary)',
          fontFamily: 'var(--font-family-mono)',
          ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
        }}
        onFocus={(e) => {
          e.currentTarget.style.boxShadow = 'var(--focus-ring)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.boxShadow = 'none';
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        data-testid={`${testId}-button`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Activity className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent)' }} />
          <div className="text-left min-w-0">
            <div className="font-medium truncate">{selectedOption.label}</div>
            <div className="text-xs truncate" style={{ color: 'var(--fg-muted)' }}>
              {selectedOption.description}
            </div>
          </div>
        </div>
        <ChevronDown
          className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")}
          style={{ color: 'var(--fg-muted)' }}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-10 max-h-60 overflow-auto"
          style={{
            backgroundColor: 'var(--bg-surface)',
            borderColor: 'var(--border-default)',
          }}
          role="listbox"
          data-testid={`${testId}-dropdown`}
        >
          {STAGE_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSelect(option.value)}
              onKeyDown={(e) => handleKeyDown(e, option.value)}
              className="w-full px-3 py-2 text-left transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: option.value === selectedStage ? 'var(--bg-elevated)' : 'transparent',
                ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = option.value === selectedStage ? 'var(--bg-elevated)' : 'transparent';
              }}
              role="option"
              aria-selected={option.value === selectedStage}
              data-testid={`${testId}-option-${option.value}`}
            >
              <div className="flex items-center gap-2">
                <Activity className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--accent)' }} />
                <div className="min-w-0">
                  <div
                    className="font-medium text-sm"
                    style={{
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--fg-primary)'
                    }}
                  >
                    {option.label}
                  </div>
                  <div
                    className="text-xs mt-0.5"
                    style={{ color: 'var(--fg-muted)' }}
                  >
                    {option.description}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Click outside handler */}
      {isOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setIsOpen(false)}
          data-testid={`${testId}-backdrop`}
        />
      )}
    </div>
  );
}